import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Video, BookOpen, Brain, Award, TrendingUp, Building } from 'lucide-react';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import VittaISEPathways from '../components/VittaISEPathways';

const ISEExam = () => {
  const iseStructuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "ISE Exam Prep Course - Institutional Securities Exam",
    "description": "Comprehensive preparation for the Institutional Securities Exam (ISE). Master institutional trading, corporate finance, and advanced securities concepts.",
    "provider": {
      "@type": "EducationalOrganization",
      "name": "Vitta Finance Academy",
      "url": "https://www.vittafinance.ca"
    },
    "courseCode": "ISE-PREP",
    "educationalLevel": "Professional",
    "about": ["Institutional Securities Exam", "Corporate Finance", "Institutional Trading"],
    "numberOfCredits": "100 MCQs in 2.5 hours",
    "offers": {
      "@type": "Offer",
      "price": "29.99",
      "priceCurrency": "CAD",
      "availability": "https://schema.org/InStock"
    }
  };

  const iseFaqData = [
    {
      question: "What is the ISE exam?",
      answer: "The Institutional Securities Exam (ISE) is a CIRO proficiency exam for investment professionals working with institutional clients such as corporations, pension funds, and large organizations. It focuses on institutional trading and corporate finance."
    },
    {
      question: "How many questions are on the ISE exam?",
      answer: "The ISE exam has 100 multiple-choice questions that must be completed in 2.5 hours. The passing score is determined by CIRO for each exam sitting."
    },
    {
      question: "Who should take the ISE exam?",
      answer: "The ISE is designed for professionals who want to work with institutional clients rather than retail investors. This includes institutional traders, corporate investment advisors, and those in corporate finance roles."
    },
    {
      question: "What is the difference between ISE and RSE?",
      answer: "The ISE focuses on institutional clients (corporations, pension funds) and larger transactions, while RSE focuses on retail clients (individuals). ISE covers more complex securities and trading concepts relevant to institutional markets."
    },
    {
      question: "How long should I study for the ISE exam?",
      answer: "We recommend 6-8 weeks of dedicated study time. The ISE requires understanding of complex institutional trading concepts and corporate finance, so thorough preparation is essential."
    },
    {
      question: "What careers can I pursue after passing the ISE?",
      answer: "After passing the ISE, you can work as an Institutional Trader, Corporate Investment Advisor, Portfolio Manager for institutional accounts, or in corporate treasury and finance roles."
    }
  ];

  return (
    <div style={{ paddingTop: '80px' }}>
      <SEO 
        title="ISE Exam Prep 2025 - Institutional Securities Exam Course | CIRO Certified"
        description="Pass the Institutional Securities Exam (ISE) with Canada's leading prep course. 100 MCQs, 2.5 hours exam. Master institutional trading, corporate finance & advanced securities. Expert video lessons, practice questions, mock exams. Perfect for institutional investment professionals."
        keywords="ISE exam, Institutional Securities Exam, CIRO ISE, ISE syllabus, ISE practice exam, ISE sample questions, ISE study guide, Institutional securities exam prep, ISE exam format, ISE exam fees, ISE mock exam, ISE question bank, ISE notes, ISE pass score, ISE exam dates, RSE vs ISE, institutional trading exam, corporate finance certification, institutional advisor exam, ISE exam prep Canada, ISE exam prep Alberta, ISE exam prep Ontario, ISE exam prep Toronto"
        ogTitle="Institutional Securities Exam (ISE) Prep Course - CIRO Certification"
        ogDescription="Complete ISE exam preparation for institutional investment professionals. Expert-led training, practice questions, full mock exams. Industry leading pass rate."
        structuredData={iseStructuredData}
        faqData={iseFaqData}
        pageType="course"
      />

      {/* Hero Section */}
      <section style={{
        padding: '60px 0',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white'
      }}>
        <div className="container">
          <Breadcrumb items={[
            { label: 'Home', link: '/' },
            { label: 'Courses', link: '/courses' },
            { label: 'ISE Exam' }
          ]} />
          
          <h1 style={{
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: 900,
            lineHeight: 1.1,
            marginBottom: '24px',
            color: 'white'
          }}>
            Institutional Securities Exam (ISE)
          </h1>
          <p style={{
            fontSize: '1.25rem',
            marginBottom: '32px',
            maxWidth: '800px',
            opacity: 0.95
          }}>
            Comprehensive preparation for institutional securities professionals. Master corporate finance, institutional trading, and complex product knowledge.
          </p>
          <div style={{
            display: 'flex',
            gap: '20px',
            flexWrap: 'wrap'
          }}>
            <a href="https://vittafinance.mykajabi.com/offers/W6ezafud" target="_blank" rel="noopener noreferrer" className="btn-gold">
              Enroll Now <ArrowRight size={20} />
            </a>
            <Link to="/contact" className="btn-secondary" style={{
              backgroundColor: 'white',
              color: 'var(--brand-primary)',
              borderColor: 'white'
            }}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* About ISE */}
      <section style={{ padding: '96px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-2" style={{ marginBottom: '24px' }}>About the ISE Exam</h2>
            <p className="body-medium" style={{ marginBottom: '20px', lineHeight: 1.8 }}>
              The <strong>Institutional Securities Exam (ISE)</strong> is designed for professionals working in institutional trading, corporate finance, and wholesale securities markets.
            </p>
            <p className="body-medium" style={{ marginBottom: '20px', lineHeight: 1.8 }}>
              This advanced exam covers complex institutional products, underwriting, corporate actions, and the regulatory framework governing institutional securities activities in Canada.
            </p>
          </div>
        </div>
      </section>

      {/* Key Topics */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--neutral-light)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '48px', textAlign: 'center' }}>ISE Exam Coverage</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
          }}>
            {[
              { icon: <Building size={32} />, title: 'Corporate Finance', desc: 'Underwriting, IPOs, secondary offerings, and debt issuance' },
              { icon: <TrendingUp size={32} />, title: 'Institutional Trading', desc: 'Block trades, algorithmic trading, and execution strategies' },
              { icon: <BookOpen size={32} />, title: 'Fixed Income Products', desc: 'Government bonds, corporate bonds, and structured products' },
              { icon: <Award size={32} />, title: 'Equity Products', desc: 'Listed equities, ADRs, and institutional equity strategies' },
              { icon: <Brain size={32} />, title: 'Regulatory Framework', desc: 'CIRO rules for institutional activities and compliance' },
              { icon: <Video size={32} />, title: 'Risk Management', desc: 'Institutional risk assessment and mitigation strategies' }
            ].map((topic, idx) => (
              <div key={idx} style={{
                backgroundColor: 'white',
                padding: '32px',
                borderRadius: '8px',
                border: '1px solid var(--border-light)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }} className="card-hover-effect">
                <div style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>{topic.icon}</div>
                <h3 className="heading-5" style={{ marginBottom: '12px' }}>{topic.title}</h3>
                <p className="body-small">{topic.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Study Pathways */}
      <VittaISEPathways />

      {/* Course Features */}
      <section style={{ padding: '96px 0' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '48px', textAlign: 'center' }}>ISE Course Features</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {[
              'Advanced case studies',
              'Institutional focus',
              'Practice questions',
              'Expert instruction',
              'Real-world scenarios',
              'Mock exams'
            ].map((feature, idx) => (
              <div key={idx} style={{
                backgroundColor: 'var(--neutral-light)',
                padding: '24px',
                borderRadius: '8px',
                border: '1px solid var(--border-light)',
                textAlign: 'center'
              }}>
                <Award size={32} color="var(--brand-primary)" style={{ marginBottom: '12px' }} />
                <p className="body-medium" style={{ fontWeight: 600 }}>{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Study Materials */}
      <section style={{ padding: '96px 0' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '16px', textAlign: 'center' }}>ISE Exam Study Materials</h2>
          <p style={{ 
            textAlign: 'center', 
            marginBottom: '48px',
            color: 'var(--text-secondary)',
            fontSize: '1.1rem'
          }}>
            Comprehensive study guides available on Amazon
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '32px'
          }}>
            {[
              {
                title: 'ISE Interactive Quick Study Guide',
                price: '49.99',
                originalPrice: '129.99',
                features: '7 Elements + Mock Exam',
                link: '/books/ise-interactive',
                isInternal: true,
                desc: 'Master ISE with 7 elements, reading notes, games, flashcards, quizzes, and mock exam with diagnostics',
                badge: 'New! April Special'
              },
              {
                title: 'ISE Mock Exam Pack',
                subtitle: '5 Full Practice Tests',
                price: '49.99',
                originalPrice: '99.99',
                features: '500 Questions',
                link: '/books/ise-mock-exam-pack',
                isInternal: true,
                desc: '5 complete mock exams with 500 practice questions covering all 7 ISE elements. Progressive difficulty with instant scoring and detailed explanations.',
                badge: '500 Questions'
              },
              {
                title: 'ISE Element Question Bank',
                subtitle: '7 Elements - 100 Questions Each',
                price: '49.99',
                originalPrice: '99.99',
                features: '700 Questions',
                link: '/books/ise-element-question-bank',
                isInternal: true,
                desc: 'Master each ISE element independently with 700 targeted practice questions. 100 questions per element with detailed explanations.',
                badge: '700 Questions'
              },
              {
                title: 'Printed ISE Study Guide',
                subtitle: 'Complete Study Book',
                desc: 'Comprehensive exam-focused study guide for the CIRO Investment Services Exam (ISE). Covers all essential topics.',
                link: 'https://a.co/d/4QD7Dva',
                price: '299.99'
              },
              {
                title: 'ISE Quick Study Guide',
                subtitle: 'Short Exam Guide',
                desc: 'High-yield revision guide distilling the most testable ISE concepts. Perfect for final week review.',
                link: 'https://www.amazon.ca/stores/Harry-Vadalkar/author/B0FB7D5BBD?ref=ap_rdr&shoppingPortalEnabled=true',
                price: null
              }
            ].map((product, idx) => (
              <div key={idx} style={{
                backgroundColor: 'white',
                padding: '32px',
                borderRadius: '12px',
                border: product.isInternal ? '2px solid var(--brand-primary)' : '1px solid var(--border-light)',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }} className="card-hover-effect">
                {product.badge && (
                  <div style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '16px',
                    background: 'linear-gradient(135deg, #d4af37 0%, #b8972e 100%)',
                    color: '#0f172a',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '700'
                  }}>
                    {product.badge}
                  </div>
                )}
                <h3 className="heading-5" style={{ marginBottom: '8px' }}>{product.title}</h3>
                <p className="caption" style={{ 
                  marginBottom: '16px', 
                  color: 'var(--brand-primary)',
                  textTransform: 'none',
                  letterSpacing: 'normal'
                }}>{product.subtitle || product.features}</p>
                <p className="body-small" style={{ color: 'var(--text-secondary)', flex: 1, marginBottom: '24px' }}>{product.desc}</p>
                {product.price && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--neutral-light)',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    textAlign: 'center'
                  }}>
                    {product.originalPrice && (
                      <p style={{ fontSize: '0.85rem', color: '#64748b', textDecoration: 'line-through', marginBottom: '4px' }}>
                        CAD ${product.originalPrice}
                      </p>
                    )}
                    <p className="caption" style={{ marginBottom: '4px' }}>{product.isInternal ? 'SPECIAL PRICE' : 'AMAZON PRICE'}</p>
                    <p style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--brand-primary)' }}>
                      CAD ${product.price}
                    </p>
                  </div>
                )}
                {product.isInternal ? (
                  <Link 
                    to={product.link}
                    className="btn-primary"
                    style={{ width: '100%', textAlign: 'center', display: 'block' }}
                  >
                    Explore Now
                  </Link>
                ) : (
                  <a 
                    href={product.link} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="btn-primary"
                    style={{ width: '100%', textAlign: 'center' }}
                  >
                    {product.price ? 'Buy on Amazon' : 'View on Amazon'}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--neutral-light)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '48px', textAlign: 'center' }}>ISE Course Pricing</h2>
          <div style={{
            maxWidth: '450px',
            margin: '0 auto'
          }}>
            <div style={{
              backgroundColor: 'var(--brand-primary)',
              padding: '48px',
              borderRadius: '16px',
              border: '2px solid var(--brand-primary)',
              textAlign: 'center',
              position: 'relative',
              boxShadow: '0 12px 40px rgba(30, 58, 138, 0.25)'
            }}>
              <div style={{
                position: 'absolute',
                top: '-14px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'var(--gold-primary)',
                color: 'white',
                padding: '8px 24px',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(212, 175, 55, 0.4)'
              }}>BEST VALUE</div>
              <h3 className="heading-4" style={{ marginBottom: '16px', color: 'white' }}>Monthly Plan</h3>
              <div style={{ fontSize: '3.5rem', fontWeight: 900, color: 'var(--gold-primary)', marginBottom: '8px' }}>$29.99</div>
              <p className="body-small" style={{ marginBottom: '32px', color: 'rgba(255,255,255,0.8)' }}>per month • Cancel anytime</p>
              <ul style={{ listStyle: 'none', padding: 0, marginBottom: '32px', textAlign: 'left' }}>
                {['All video lessons', 'AI Tutor access', 'Practice questions', 'Mock exams', 'Study guides'].map((feature, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                    <Award size={22} color="var(--gold-primary)" />
                    <span className="body-small" style={{ color: 'white', fontSize: '1rem' }}>{feature}</span>
                  </li>
                ))}
              </ul>
              <a href="https://vittafinance.mykajabi.com/offers/W6ezafud" target="_blank" rel="noopener noreferrer" className="btn-gold" style={{ width: '100%', padding: '16px 32px', fontSize: '1.1rem' }}>Get Started</a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--bg-page)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '16px', textAlign: 'center' }}>
            Frequently Asked Questions
          </h2>
          <p className="body-large" style={{ 
            marginBottom: '48px', 
            textAlign: 'center',
            color: 'var(--text-secondary)',
            maxWidth: '600px',
            margin: '0 auto 48px'
          }}>
            Everything you need to know about the ISE exam
          </p>
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {iseFaqData.map((faq, idx) => (
              <details 
                key={idx}
                style={{
                  backgroundColor: 'var(--bg-card)',
                  borderRadius: '12px',
                  border: '1px solid var(--border-light)',
                  overflow: 'hidden'
                }}
              >
                <summary style={{
                  padding: '20px 24px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '1.05rem',
                  color: 'var(--text-primary)',
                  listStyle: 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  {faq.question}
                  <span style={{ 
                    fontSize: '1.5rem', 
                    color: 'var(--brand-primary)',
                    fontWeight: 400
                  }}>+</span>
                </summary>
                <div style={{
                  padding: '0 24px 20px',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.7
                }}>
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{
        padding: '96px 0',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '24px', color: 'white' }}>Advance Your Institutional Career</h2>
          <p style={{ 
            fontSize: '1.25rem',
            lineHeight: 1.7,
            marginBottom: '40px', 
            maxWidth: '700px', 
            margin: '0 auto 40px',
            color: 'white',
            fontWeight: 500
          }}>
            Join thousands of successful institutional professionals who passed with Vitta Finance Academy
          </p>
          <a href="https://www.vittafinance.ca/store" target="_blank" rel="noopener noreferrer" className="btn-gold">
            Enroll in ISE Course <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default ISEExam;